// Make POs (ERP-PLAN §5.11): the parts of a build whose documents its CM gets with the make PO, beside the built item's
// own spec. A PCBA the CM builds inside the build (a phantom sub-assembly) carries the Gerbers, BOM + placement and
// firmware; a box or PCB the CM buys itself carries what it's printed or made from. What HHL sends arrives finished,
// a service has no spec, and an HHL product label comes as the labels HHL Ops makes (shared/product-labels.ts); a bag
// or box carrying one is still listed (the CM buys it and sticks the label on).
// Each sent line pins these parts' spec revisions (po_line.part_specs) like its own spec_revision_id: the server at
// "Mark as sent" (server/src/mutations/po-docs.ts), the browser to show a draft's (web/src/pages/po/docs.ts).
import { type BomIndex, type BomItem, type BomRow, byCm, resolve } from "./boms.ts";
import { isLabelOnly } from "./product-labels.ts";

/** A build part a sent line carries: its item and the spec revision the CM gets (null: none was released when pinned). */
export type PartSpec = { item_id: string; spec_revision_id: string | null };

/**
 * The parts of `item` built from `bom` (a make order's revision) whose documents the CM gets: phantom sub-assemblies it
 * makes in the build and parts it buys or makes itself (a "CM buys" or "CM makes" line, or anything under a phantom on a
 * "CM buys" line), in BOM order,
 * each once.
 */
export function buildDocParts(ix: BomIndex, bom: BomRow, item: BomItem): BomItem[] {
  const out = new Map<string, BomItem>();
  for (const l of resolve(ix, bom, bom.shopify_product_id ? item : undefined).lines) {
    if (!l.phantom && !byCm(l.supply)) continue;
    if (l.item.kind === "service" || isLabelOnly(l.item) || l.item.id === item.id) continue;
    out.set(l.item.id, l.item);
  }
  return [...out.values()];
}

/** A line's part_specs as stored (null until it's sent). */
export const partSpecsOf = (v: unknown): PartSpec[] | null => (Array.isArray(v) ? (v as PartSpec[]) : null);
